import React, { useState, useMemo, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FilteredRolodexView } from './FilteredRolodexView';
import { Radar, Building2, Calendar, Handshake, List, Grid3x3, Map, MapPin } from 'lucide-react';

/**
 * AroundMeView Component
 *
 * Displays businesses, events, and barters around the user's location with multiple view modes:
 * - Rolodex: Vertical card-based carousel
 * - Grid: Compact grid layout
 * - Map: Interactive map view
 *
 * Includes category filtering functionality
 */
export const AroundMeView = ({
    businesses = [],
    events = [],
    barters = [],
    zones = [],
    categories = [],
    bartersCategories = [],
    chatId,
    userLocation = null,
    radiusKm = 5
}) => {
    const [viewMode, setViewMode] = useState('rolodex'); // 'rolodex', 'compact', 'map'
    const [showFilters, setShowFilters] = useState(false);
    const [activeTab, setActiveTab] = useState('businesses'); // 'businesses', 'events', 'barters', or 'zones'

    // Switch to compact view when zones tab is selected and rolodex is active
    useEffect(() => {
        if (activeTab === 'zones' && viewMode === 'rolodex') {
            setViewMode('compact');
        }
    }, [activeTab, viewMode]);

    // Prepare items based on active tab
    const allItems = useMemo(() => {
        let itemsToProcess;
        let itemType;

        if (activeTab === 'businesses') {
            itemsToProcess = businesses;
            itemType = 'business';
        } else if (activeTab === 'events') {
            itemsToProcess = events;
            itemType = 'event';
        } else if (activeTab === 'barters') {
            itemsToProcess = barters;
            itemType = 'barter';
        } else if (activeTab === 'zones') {
            itemsToProcess = zones;
            itemType = 'zone';
        }

        if (!itemsToProcess) return [];

        return itemsToProcess.map(item => ({
            ...item,
            type: itemType,
            // FilteredRolodexView expects location as JSON string
            // For zones, the location structure is different (longitude/latitude instead of lng/lat)
            location: item.location ? JSON.stringify({
                lat: item.location.latitude || item.location.lat,
                lng: item.location.longitude || item.location.lng
            }) : null,
            // For zones, use zone_id as the business_name for display
            business_name: itemType === 'zone' ? item.zone_id : item.business_name,
            // Add distance info to description for zones
            business_description: itemType === 'zone'
                ? `מרחק: ${item.distance} ק"מ`
                : item.business_description
        }));
    }, [businesses, events, barters, zones, activeTab]);

    // View mode toggle buttons config
    const viewModes = [
        { mode: 'rolodex', label: 'מורחבת', icon: <List size={14} />, tooltip: 'תצוגת גלילה אופקית' },
        { mode: 'compact', label: 'מצומצמת', icon: <Grid3x3 size={14} />, tooltip: 'תצוגת רשת קומפקטית' },
        { mode: 'map', label: 'מפה', icon: <Map size={14} />, tooltip: 'תצוגת מפה' }
    ].filter(mode => {
        // For zones, only show compact and map views
        if (activeTab === 'zones') {
            return mode.mode === 'compact' || mode.mode === 'map';
        }
        return true;
    });

    return (
        <div className="w-full">
            {/* Tab Navigation - Businesses / Events / Barters / Zones - Matching ZoneDetails style */}
            <div className="sticky top-0 z-20 bg-surface/95 backdrop-blur-sm border-b-2 border-transparent">
                <div className="flex items-center justify-center gap-1 px-4 py-3">
                    {[
                        { id: 'businesses', label: 'עסקים', icon: <Building2 size={16} /> },
                        { id: 'events', label: 'מפגשים', icon: <Calendar size={16} /> },
                        { id: 'barters', label: 'בארטרים', icon: <Handshake size={16} /> },
                        { id: 'zones', label: 'קהילות', icon: <MapPin size={16} /> },
                    ].map(tab => (
                        <motion.button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            className={`
                                relative flex items-center gap-2 px-4 py-2.5 rounded-xl
                                font-medium text-sm transition-all duration-300
                                ${activeTab === tab.id
                                    ? 'text-ink-strong'
                                    : 'text-ink-muted hover:text-ink-strong'
                                }
                            `}
                        >
                            {tab.icon}
                            <span>{tab.label}</span>
                            {activeTab === tab.id && (
                                <motion.div
                                    layoutId="activeTabUnderline"
                                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-secondary-500"
                                    transition={{ type: "spring", stiffness: 380, damping: 30 }}
                                />
                            )}
                        </motion.button>
                    ))}
                </div>
            </div>

            {/* Control Bar: View Mode + Filters - Matches ZoneDetails styling */}
            <div className="bg-white/60 border-b border-line py-3 mb-4">
                <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    className="flex items-center justify-center sm:justify-between gap-3 flex-wrap w-full max-w-4xl mx-auto px-2"
                >
                    {/* View Mode Selector */}
                    <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-ink-muted hidden sm:block">תצוגה:</span>
                        <div className="inline-flex items-center gap-1 bg-white rounded-2xl p-1 shadow-soft border border-line">
                            {viewModes.map(({ mode, label, icon, tooltip }) => (
                                <button
                                    key={mode}
                                    onClick={() => setViewMode(mode)}
                                    title={tooltip}
                                    className={`
                                        flex items-center gap-1.5 px-3 py-1.5 rounded-xl
                                        text-xs font-semibold transition-all duration-200
                                        ${viewMode === mode
                                            ? 'bg-secondary-500 text-white shadow-sm'
                                            : 'text-ink-muted hover:bg-surface-muted hover:text-secondary-500'
                                        }
                                    `}
                                >
                                    {icon}
                                    {/* <span>{label}</span> */}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Filter Toggle Button - hidden for zones tab */}
                    {activeTab !== 'zones' && (
                        <button
                            type="button"
                            onClick={() => {
                                const currentCategories = activeTab === 'barters' ? bartersCategories : categories;
                                if (currentCategories && currentCategories.length > 0) {
                                    setShowFilters(!showFilters);
                                }
                            }}
                            disabled={activeTab === 'barters' ? (!bartersCategories || bartersCategories.length === 0) : (!categories || categories.length === 0)}
                            title={
                                (activeTab === 'barters' ? (!bartersCategories || bartersCategories.length === 0) : (!categories || categories.length === 0))
                                    ? 'אין קטגוריות זמינות לסינון'
                                    : (showFilters ? 'הסתרת אפשרויות סינון' : 'הצגת אפשרויות סינון')
                            }
                            className={`
                                flex items-center gap-2 px-4 py-1.5 rounded-xl
                                text-xs font-semibold transition-all duration-300 shadow-soft
                                ${showFilters
                                    ? 'bg-secondary-500 text-white scale-105'
                                    : 'bg-white text-ink-muted border border-line hover:bg-surface-muted hover:border-line-strong hover:text-secondary-500'
                                }
                                ${(activeTab === 'barters' ? (!bartersCategories || bartersCategories.length === 0) : (!categories || categories.length === 0)) ? 'opacity-50 cursor-not-allowed hover:bg-white hover:text-ink-muted' : ''}
                            `}
                        >
                            <span>סינון</span>
                            {activeTab === 'barters' ? (
                                bartersCategories && bartersCategories.length > 0 && (
                                    <span className={`text-[10px] font-medium ${showFilters ? 'text-white/80' : 'text-ink-subtle'}`}>
                                        {bartersCategories.length}
                                    </span>
                                )
                            ) : (
                                categories && categories.length > 0 && (
                                    <span className={`text-[10px] font-medium ${showFilters ? 'text-white/80' : 'text-ink-subtle'}`}>
                                        {categories.length}
                                    </span>
                                )
                            )}
                            {showFilters && <span className="text-xs">✓</span>}
                        </button>
                    )}
                </motion.div>
            </div>

            {/* Main Content: FilteredRolodexView */}
            {allItems.length > 0 ? (
                <FilteredRolodexView
                    items={allItems}
                    chatId={chatId}
                    categories={activeTab === 'barters' ? bartersCategories : categories}
                    type={activeTab === 'businesses' ? 'business' : activeTab === 'events' ? 'event' : activeTab === 'zones' ? 'zone' : 'barter'}
                    viewMode={viewMode}
                    onViewModeChange={setViewMode}
                    showFilters={showFilters}
                    onToggleFilters={() => setShowFilters(!showFilters)}
                    showMap={true}
                    zoneCenter={userLocation ? [userLocation[0], userLocation[1]] : null}
                />
            ) : (
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center py-12 bg-gradient-to-br from-surface-muted to-white rounded-2xl shadow-md border border-line-subtle"
                >
                    <Radar size={48} className="text-ink-subtle mx-auto mb-3" />
                    <p className="text-ink-muted text-lg font-semibold mb-1">
                        {activeTab === 'businesses' ? 'לא נמצאו עסקים בסביבתך' :
                            activeTab === 'events' ? 'לא נמצאו מפגשים בסביבתך' :
                                activeTab === 'zones' ? 'לא נמצאו קהילות בסביבתך' :
                                    'לא נמצאו בארטרים בסביבתך'}
                    </p>
                    <p className="text-ink-subtle text-sm">
                        נסה להגדיל את רדיוס החיפוש
                    </p>
                </motion.div>
            )}
        </div>
    );
};
